import { memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { FilterTrigger } from '~/components/Filters/SingleFilters/FilterTrigger';
import { SingleTypeFilter, type SingleTypeFilterProps } from '~/components/Filters/TypeFilter/SingleTypeFilters';
import { TYPE_FILTERS } from '~/components/Filters/types';
import { baseFiltersOptions } from '~/components/FiltersMenu/types';
import { useFilterParams } from '~/hooks/filters/useFilterParams';
import { removeFiltersAction } from '~/store/filters/actions';
import { type SelectedFilter } from '~/store/filters/types';

export type TypeFilterTriggerProps = SingleTypeFilterProps;

export const TypeFilterTrigger = memo(
  ({ availableTypes, onApplyClick, onRemoveClick, onOpenChange }: TypeFilterTriggerProps) => {
    const dispatch = useDispatch();
    const { typesParam, assetFileKindParam } = useFilterParams();

    const count = useMemo(() => {
      const typeCount = typesParam.reduce((acc, type) => {
        if (type === 'boards') {
          acc++;
        } else {
          acc += Object.values(TYPE_FILTERS[type].subFilters ?? {}).length;
        }

        return acc;
      }, 0);

      const assetFileKindCount = [assetFileKindParam?.is, ...(assetFileKindParam?.or ?? [])].filter(Boolean).length;

      return typeCount + assetFileKindCount;
    }, [assetFileKindParam?.is, assetFileKindParam?.or, typesParam]);

    const content = useMemo(
      () => <SingleTypeFilter availableTypes={availableTypes} onApplyClick={onApplyClick} />,
      [availableTypes, onApplyClick],
    );

    const removeFilter = useCallback(async () => {
      const filtersToRemove: SelectedFilter[] = [{ type: 'type' }, { type: 'assetFileKind' }];

      dispatch(removeFiltersAction({ filters: filtersToRemove.map((filter) => filter.type), customFields: [] }));

      onRemoveClick?.(filtersToRemove);
    }, [dispatch, onRemoveClick]);

    return (
      <FilterTrigger
        onRemoveClick={removeFilter}
        count={count}
        filterContent={content}
        title={baseFiltersOptions.type.label}
        Icon={baseFiltersOptions.type.icon}
        onOpenChange={onOpenChange}
      />
    );
  },
);

TypeFilterTrigger.displayName = 'TypeFilterTrigger';
